import { PlanMealTime } from "@/clients";
import { MealTime } from "@/clients/models/MealTime";
import { FavouritesIcon } from "@/components/icons/favourites-icon";
import { FilledButton } from "@/components/ui/buttons";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo } from "react";

interface AddToPlanMealItemProps {
  mealTime: MealTime;
  meals: PlanMealTime[];
  onSelect: (mealTime: MealTime, mealIndex: number) => void;
  isUpdating: boolean;
  showAllSlots?: boolean;
  yourFoodMealsSet?: Set<string>;
}

export const AddToPlanMealItem = memo(function AddToPlanMealItem({
  mealTime,
  meals,
  onSelect,
  isUpdating,
  showAllSlots = false,
  yourFoodMealsSet = new Set(),
}: AddToPlanMealItemProps) {
  const mealsOnMealTime = meals.filter((meal) => meal.mealTime === mealTime);

  // Get meal time label
  const getMealTimeLabel = (mealTime: MealTime) => {
    switch (mealTime) {
      case MealTime.BREAKFAST:
        return "Breakfast";
      case MealTime.LUNCH:
        return "Lunch";
      case MealTime.DINNER:
        return "Dinner";
      case MealTime.SNACK:
        return "Snack";
      default:
        return mealTime;
    }
  };

  if (showAllSlots && mealTime === MealTime.SNACK) {
    const maxSnacks = 4;
    const snackSlots = Array.from({ length: maxSnacks }, (_, index) => {
      const existingMeal = mealsOnMealTime[0]?.data[index];
      return {
        index,
        meal: existingMeal,
      };
    });

    return (
      <>
        {snackSlots.map(({ index, meal }) => {
          const isMyFood = meal?.id ? yourFoodMealsSet.has(meal.id) : false;

          return (
            <div
              key={`${mealTime}-${index}`}
              className={cn(
                "flex items-center justify-between rounded-2xl bg-white px-4 py-3 shadow-sm",
                {
                  "shadow-[-6px_0_8px_-6px_var(--color-your-foods)]": isMyFood,
                },
              )}
            >
              <div className="flex flex-1 flex-col gap-1">
                <p className={cn(textVariants.bodySmall, "font-semibold text-gray-900")}>
                  {getMealTimeLabel(mealTime)}
                </p>
                {meal && (
                  <div className="flex items-center gap-2">
                    {isMyFood && (
                      <FavouritesIcon filled={true} className="fill-your-foods h-4.5 w-4.5" />
                    )}
                    <p className={cn(textVariants.bodySmall, "text-gray-600")}>{meal.name}</p>
                  </div>
                )}
              </div>

              <FilledButton
                color="black"
                textCase="normal"
                onClick={() => onSelect(mealTime, index)}
                disabled={isUpdating}
                className="h-10 w-20 text-sm"
              >
                Select
              </FilledButton>
            </div>
          );
        })}
      </>
    );
  }

  const mealData = mealsOnMealTime[0]?.data[0];
  const isMyFood = mealData?.id ? yourFoodMealsSet.has(mealData.id) : false;

  return (
    <div
      className={cn("flex items-center justify-between rounded-2xl bg-white px-4 py-3 shadow-sm", {
        "shadow-[-6px_0_8px_-6px_var(--color-your-foods)]": isMyFood,
      })}
    >
      <div className="flex flex-1 flex-col gap-3">
        <h5 className={cn(textVariants.listItemTitle, "font-blod")}>
          {getMealTimeLabel(mealTime)}
        </h5>
        {mealData && (
          <div className="flex items-center gap-2">
            {isMyFood && <FavouritesIcon filled={true} className="fill-your-foods h-4.5 w-4.5" />}
            <p className={cn(textVariants.footnote, "text-gray-600")}>{mealData.name}</p>
          </div>
        )}
      </div>

      <FilledButton
        color="black"
        textCase="normal"
        onClick={() => onSelect(mealTime, 0)}
        disabled={isUpdating}
        className="h-10 w-20"
      >
        Select
      </FilledButton>
    </div>
  );
});
